import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { TID } from "@/constants/testIds";
import { PiggyBank, ShieldCheck, ChartBar, LockKey } from "@phosphor-icons/react";

export default function LoginPage() {
  const { user, login, error } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && typeof user === "object") return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) nav("/dashboard");
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left panel */}
      <div className="hidden md:flex flex-col justify-between p-12 brand-gradient text-white relative overflow-hidden">
        <div className="absolute inset-0 subtle-grid-bg opacity-10" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
              <PiggyBank weight="fill" size={26} />
            </div>
            <div>
              <div className="heading text-xl font-semibold leading-tight">Ujjwal Multipurpose</div>
              <div className="text-[11px] uppercase tracking-widest text-white/70">Cooperative Society Ltd</div>
            </div>
          </div>
        </div>
        <div className="relative space-y-6">
          <h1 className="heading text-4xl font-semibold leading-tight max-w-md">
            Deposits, loans, and daily collections — one operational hub.
          </h1>
          <div className="grid grid-cols-1 gap-3 max-w-md">
            <Feature icon={PiggyBank} title="Savings: Daily / RD / FD" desc="With SI or compound interest and manual overrides." />
            <Feature icon={ChartBar} title="Loans: Deposit / General / Commodity" desc="Full EMI schedule, ledger and overdue tracking." />
            <Feature icon={ShieldCheck} title="Role hierarchy & permissions" desc="Super admin → Branch admin → Agent → Staff." />
          </div>
        </div>
        <div className="relative text-xs text-white/60">© Ujjwal Multipurpose Cooperative Society Ltd · Internal staff platform</div>
      </div>

      {/* Right panel — login form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="md:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl brand-gradient flex items-center justify-center shrink-0">
              <PiggyBank weight="fill" className="text-white" size={22} />
            </div>
            <div>
              <div className="heading text-base font-semibold">Ujjwal Multipurpose</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Cooperative Society Ltd</div>
            </div>
          </div>

          <h2 className="heading text-2xl font-semibold text-slate-900">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">Access the operations dashboard.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <div className="text-xs font-medium text-slate-600 mb-1.5">Email</div>
              <input
                data-testid={TID.loginEmail}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <div className="text-xs font-medium text-slate-600 mb-1.5">Password</div>
              <input
                data-testid={TID.loginPassword}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div data-testid={TID.loginError} className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <button
              type="submit"
              data-testid={TID.loginSubmit}
              disabled={loading}
              className="w-full bg-emerald-700 text-white font-medium py-2.5 rounded-lg hover:bg-emerald-800 disabled:opacity-60 transition-colors"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-8 flex items-start gap-2 text-xs text-slate-500 border-t border-slate-100 pt-4">
            <LockKey size={14} className="mt-0.5 shrink-0 text-slate-400" />
            <div>
              This is an internal staff platform. New staff accounts are created by the
              Super Admin from within the app — there is no public sign-up. If you need
              access, ask your Super Admin to add you to the Users list.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-3.5 border border-white/10">
      <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
        <Icon size={18} weight="regular" />
      </div>
      <div>
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs text-white/70 mt-0.5">{desc}</div>
      </div>
    </div>
  );
}
